/*
  Norse Quiz — answer state and results-page personalisation.

  Split from norse-quiz.js on purpose: the results page needs this file even when
  the quiz overlay never opens (a returning visitor whose answers are still in
  sessionStorage), and a results section should be able to personalise itself
  without pulling in the whole quiz engine.

  The personalisation model is the one the theme already uses for gender
  (assets/norse-gender-switch.js): a single source of truth published onto
  <html> as data attributes, with everything else reacting to it. Here that is
  `data-nq-<questionId>`, so a results section can be shown or hidden by an
  attribute selector alone and a new quiz needs no new JavaScript.

  Multi-select answers are written space separated so CSS can match one value
  out of many with `~=`:

    html[data-nq-scar-type~='keloid'] .my-block { ... }

  Three hooks are available to results markup:

    data-nq-when="severity:moderate|severe"   show only for those answers
    data-nq-when="!severity:mild"             show for anything but that answer
    data-nq-when="a:x; b:y, c:z"              a AND (b OR c) — `;` ANDs, `,` ORs
    data-nq-token="first_name"                replaced with the captured value

  Conditions are ANDed when separated by `;`.
*/
(function () {
  'use strict';

  if (window.NorseQuiz) return;

  var STORAGE_KEY = 'norse-quiz';
  var EVENT_COMPLETE = 'norse:quiz-complete';
  var EVENT_APPLIED = 'norse:quiz-applied';

  /** sessionStorage, not localStorage: a quiz answer should not outlive the visit. */
  function read() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function write(state) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      /* Private mode and full quotas are not worth breaking the quiz over. */
    }
  }

  function clear() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* no-op */
    }
  }

  /** `Scar Type` / `scar_type` -> `scar-type`, so it is a legal data attribute. */
  function toAttributeName(key) {
    return String(key || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  function valuesOf(answer) {
    if (answer == null) return [];
    if (Array.isArray(answer)) return answer.map(String).filter(Boolean);
    return [String(answer)].filter(Boolean);
  }

  /** Publish every answer onto <html> as `data-nq-<question>`. */
  function paintAttributes(state) {
    var root = document.documentElement;
    var answers = (state && state.answers) || {};

    Object.keys(answers).forEach(function (key) {
      var name = toAttributeName(key);
      if (!name) return;
      var values = valuesOf(answers[key]);
      if (values.length === 0) {
        root.removeAttribute('data-nq-' + name);
        return;
      }
      root.setAttribute('data-nq-' + name, values.join(' '));
    });

    if (state && state.completed) root.setAttribute('data-nq-completed', 'true');
  }

  /**
   * One `question:value|value` clause. A leading `!` negates it. A clause with
   * no colon just asserts the question was answered at all.
   */
  function matchesClause(clause, answers) {
    var negated = clause.charAt(0) === '!';
    if (negated) clause = clause.slice(1);

    var parts = clause.split(':');
    var key = (parts[0] || '').trim();
    if (!key) return true;

    var given = valuesOf(answers[key]);
    var result;

    if (parts.length < 2) {
      result = given.length > 0;
    } else {
      var wanted = parts[1]
        .split('|')
        .map(function (value) {
          return value.trim();
        })
        .filter(Boolean);
      result = given.some(function (value) {
        return wanted.indexOf(value) !== -1;
      });
    }

    return negated ? !result : result;
  }

  /*
    `;` separates clauses that must ALL hold; `,` inside a clause separates
    alternatives, of which ONE is enough. So the skin quiz's add-on rule —
    "not Other, and (moderate/severe OR very important)" — is one attribute:

      data-nq-when="!gender:other; severity:moderate|severe, importance:very"

    An OR across two different questions cannot be written any other way: `|`
    only ORs values inside one question. `,` was unused, so this is additive.
  */
  function matches(expression, answers) {
    return String(expression || '')
      .split(';')
      .map(function (clause) {
        return clause.trim();
      })
      .filter(Boolean)
      .every(function (clause) {
        return clause
          .split(',')
          .map(function (alternative) {
            return alternative.trim();
          })
          .filter(Boolean)
          .some(function (alternative) {
            return matchesClause(alternative, answers);
          });
      });
  }

  /** Hide the branches this shopper's answers do not select. */
  function applyConditions(state, scope) {
    var answers = (state && state.answers) || {};
    var nodes = (scope || document).querySelectorAll('[data-nq-when]');

    Array.prototype.forEach.call(nodes, function (node) {
      var show = matches(node.getAttribute('data-nq-when'), answers);
      node.hidden = !show;
    });
  }

  /** Fill `data-nq-token` placeholders from captured fields and computed tokens. */
  function applyTokens(state, scope) {
    var tokens = (state && state.tokens) || {};
    var nodes = (scope || document).querySelectorAll('[data-nq-token]');

    Array.prototype.forEach.call(nodes, function (node) {
      var key = node.getAttribute('data-nq-token');
      var value = tokens[key];
      if (value == null || value === '') {
        // A token with no value falls back to whatever the server rendered, so
        // an un-answered quiz still reads as clean copy rather than a gap.
        if (node.dataset.nqTokenFallback != null) node.textContent = node.dataset.nqTokenFallback;
        return;
      }
      node.textContent = String(value);
    });
  }

  function apply(scope) {
    var state = read();
    paintAttributes(state);
    applyConditions(state, scope);
    applyTokens(state, scope);
    document.dispatchEvent(
      new CustomEvent(EVENT_APPLIED, { detail: { state: state }, bubbles: true })
    );
    return state;
  }

  window.NorseQuiz = {
    STORAGE_KEY: STORAGE_KEY,
    EVENT_COMPLETE: EVENT_COMPLETE,
    EVENT_APPLIED: EVENT_APPLIED,
    read: read,
    write: write,
    clear: clear,
    matches: matches,
    apply: apply,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      apply();
    });
  } else {
    apply();
  }

  // Re-apply after the quiz finishes and after a Theme Editor section reload.
  document.addEventListener(EVENT_COMPLETE, function () {
    apply();
  });
  document.addEventListener('shopify:section:load', function (event) {
    apply(event.target);
  });
})();
